import { defineComponent as _defineComponent } from 'vue'
import { AxiosResponse } from 'axios';
import useVuelidate, { Validation, ValidationArgs } from '@vuelidate/core';
import { maxLength, minLength, required } from '@vuelidate/validators';
import { computed, onMounted, reactive, Ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { toast, toastServiceError } from '@/services';
import { useUiStore } from '@/stores';
import { TrashIcon } from '@heroicons/vue/outline';
import ScriptService from '@/services/api/ScriptService';
import {
    FormAction,
    MultiSelectOption,
    Permission,
    ScriptFormState,
    ScriptNumberFormState,
} from '@/types';


export default /*@__PURE__*/_defineComponent({
  __name: 'ScriptFormPage',
  props: {
    action: { type: String, default: FormAction.CREATE },
},
  setup(__props, { expose: __expose }) {
  __expose();

const { t } = useI18n();
const uiStore = useUiStore();
const route = useRoute();
const router = useRouter();

const props = __props;

const mediaOptions = [
    {
        label: t('script.edit.form.media.options.direto'),
        value: 'direto',
    },
    {
        label: t('script.edit.form.media.options.facebook'),
        value: 'facebook',
    },
    {
        label: t('script.edit.form.media.options.gclid'),
        value: 'gclid',
    },
    {
        label: t('script.edit.form.media.options.organico'),
        value: 'organico',
    },
] as MultiSelectOption[];

const defaultMediaOption = mediaOptions.findIndex((option) => option.value === 'gclid');
const formState = reactive<ScriptFormState>({
    clientUrl: '',
    isActive: true,
    includeJquery: false,
    numbers: [
        {
            media: mediaOptions[defaultMediaOption],
            fromNumber: '',
            toNumber: '',
        },
    ],
});

const rules = computed<ValidationArgs<ScriptFormState>>(() => ({
    clientUrl: { required, maxLength: maxLength(255) },
    isActive: { required },
    includeJquery: { required },
    numbers: { required, minLength: minLength(1) },
}));

const v$: Ref<Validation<ValidationArgs<ScriptFormState>>> = useVuelidate(rules, formState);

const numberRules: ValidationArgs<ScriptNumberFormState> = {
    media: { required, maxLength: maxLength(250) },
    fromNumber: { required, maxLength: maxLength(20) },
    toNumber: { required, maxLength: maxLength(20) },
};

let vv$ = formState.numbers.map((number) => useVuelidate(numberRules, number));

watch(
    () => formState.numbers,
    () => {
        vv$ = formState.numbers.map((number) => useVuelidate(numberRules, number));
    },
    { deep: true },
);

const setLoading = (loading: boolean) => uiStore.setIsLoading(loading);

const fetchScriptById = async (id: number) => {
    try {
        setLoading(true);
        const { data } = await ScriptService.get<ScriptFormState>(id);
        const formData = {
            ...data,
            numbers: data.numbers.map((number) => ({
                ...number,
                media: mediaOptions.find((option) => option.value === number.media),
            })),
        };
        Object.assign(formState, formData);
    } catch (error) {
        toastServiceError(error);
    } finally {
        setLoading(false);
    }
};

onMounted(async () => {
    if (props.action === FormAction.EDIT) {
        const scriptId = Number(route.params.id);
        if (!isNaN(scriptId)) fetchScriptById(scriptId);
    }
});

const areAllValidationsCorrect = async (
    validationsArray: Ref<Validation<ValidationArgs<ScriptNumberFormState>>>[],
): Promise<boolean> => {
    const validationPromises = validationsArray.map((validation) => validation.value.$validate());
    await Promise.all(validationPromises);
    return validationsArray.every((validation) => validation.value.$invalid === false);
};

const removeNumber = (index: number) => {
    formState.numbers.splice(index, 1);
};

const addNumber = () => {
    formState.numbers.push({
        media: mediaOptions[0],
        fromNumber: '',
        toNumber: '',
    });
};

const onSubmit = async () => {
    const isFormCorrect = await v$.value.$validate();
    const isNumberFormCorrect = await areAllValidationsCorrect(vv$);

    if (!isFormCorrect || !isNumberFormCorrect) return;

    try {
        await setLoading(true);
        let response: AxiosResponse;

        const payload = {
            ...formState,
            numbers: formState.numbers.map((number) => ({
                ...number,
                media: number.media.value,
            })),
        };

        const handleServiceSuccess = (message: string, id: number | undefined) => {
            toast.success(message);
            v$.value.$reset();
            router.push({
                name: 'ViewScriptById',
                params: { id },
            });
        };

        if (props.action === FormAction.EDIT) {
            const id = Number(route.params.id);
            response = await ScriptService.update(id, payload);
            handleServiceSuccess(response.data.message, id);
        } else {
            response = await ScriptService.create(payload);
            handleServiceSuccess(response.data.message, response.data.result.id);
        }
    } catch (error) {
        toastServiceError(error);
    } finally {
        setLoading(false);
    }
};

const __returned__ = { t, uiStore, route, router, props, mediaOptions, defaultMediaOption, formState, rules, v$, numberRules, get vv$() { return vv$ }, set vv$(v) { vv$ = v }, setLoading, fetchScriptById, areAllValidationsCorrect, removeNumber, addNumber, onSubmit, get TrashIcon() { return TrashIcon }, get FormAction() { return FormAction }, get Permission() { return Permission } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})